import { useMemo } from "react";
import {
  COST_SWR_BASE_CONFIG,
  appendDateRangeParams,
  useCostAuthHeaders,
  VITE_COST_AND_USAGE_API,
} from "rwb/pages/project/hooks/Shared/useCosts";
import useSWR from "swr";
import { camelizingFetcher } from "~/hooks/useFetcher";
import { isApiError } from "~/utils/apiClient";
import { CostGranularity, monthsToDate, ProjectCostBreakdownResponse } from "./costSummary";

/**
 * Project cost per day, week or month for the last `months` calendar months
 * (current month to date included), plus cost per user. Resolves to null when
 * the project has no cost in the range. Pass a null `projectUuid` to skip
 * fetching. The endpoint takes the project UUID, not its short id.
 */
export const useProjectCostBreakdown = (
  projectUuid: string | null,
  months: number,
  granularity: CostGranularity
) => {
  const headers = useCostAuthHeaders();
  const { startDate, endDate } = useMemo(() => monthsToDate(months, new Date()), [months]);
  const params = new URLSearchParams();
  appendDateRangeParams(params, startDate, endDate);
  params.set("granularity", granularity);
  const url = projectUuid
    ? `${VITE_COST_AND_USAGE_API}/v1/projects/${projectUuid}/cost/breakdown?${params}`
    : null;

  return useSWR<ProjectCostBreakdownResponse | null>(
    url,
    async (breakdownUrl: string) => {
      try {
        return await camelizingFetcher<ProjectCostBreakdownResponse>({}, { headers })(breakdownUrl);
      } catch (error) {
        // The service answers 404 for a range with no cost data; the caller
        // is a confirmed owner, so that just means no cost yet.
        if (isApiError(error) && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    COST_SWR_BASE_CONFIG
  );
};
